/**
 * React Query hooks for Settings > Claude (/api/settings/mcp, leads worker).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/hooks/use-api'
import type { McpConnection, McpCreateResponse, McpSettingsResponse } from './mcp-helpers'

export const mcpKeys = { list: ['settings', 'mcp'] as const }

export function useMcpConnections(enabled = true) {
  const api = useApi()
  return useQuery({
    queryKey: mcpKeys.list,
    queryFn: async () => {
      const res = await api<McpSettingsResponse>('/api/settings/mcp')
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load Claude connections')
      return res.data
    },
    enabled,
    staleTime: 30_000,
  })
}

export function useCreateMcpConnection() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { label: string; write: boolean; days: number }) => {
      const res = await api<McpCreateResponse>('/api/settings/mcp', { method: 'POST', body: input })
      if (!res.success || !res.data) throw new Error(res.error ?? 'Could not create the connection')
      return res.data
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: mcpKeys.list }),
  })
}

export function useUpdateMcpConnection() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: string; write?: boolean; skip_qg?: boolean; acknowledge?: boolean }) => {
      const res = await api<{ connection: McpConnection; writes_enabled: boolean; note?: string }>(
        `/api/settings/mcp/${id}`,
        { method: 'PATCH', body },
      )
      if (!res.success || !res.data) throw new Error(res.error ?? 'Could not update the connection')
      return res.data
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: mcpKeys.list }),
  })
}

export function useRevokeMcpConnection() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api<{ revoked: boolean }>(`/api/settings/mcp/${id}`, { method: 'DELETE' })
      if (!res.success) throw new Error(res.error ?? 'Could not revoke the connection')
      return res.data
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: mcpKeys.list }),
  })
}
